<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue'
import { useBrxmData } from '../../composables/useBrxmData'
import { useImage } from '../../composables/useImageUrl'
import { EDesktopImagePosition, EDesktopLayout, EMobileImagePosition } from './ts/types'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import { getLinkTarget } from '@/cms-ui/utils/linkTarget'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'
import { useDrawer } from '@/cms-ui/composables/useDrawer'

const props = defineProps<IBrxmProps>()
const data = useBrxmData(props, 'STEPS')
const { isTablet, isDesktop } = useBreakpoint()
const { desktopLayout, mobileImagePosition, desktopImagePosition, darkmode, imageOrVideo, steps, link, button } =
    data.value
const { image: responsiveImage } = useImage(imageOrVideo, props.page)
const currentStep = ref<number>()
const stepsRef = useTemplateRef('stepsRef')

useVisibilityTracking(stepsRef, 'Steps', 'Steps')

const { openDrawer } = useDrawer()

const formattedStepsData = steps.map((item: any, index: number) => ({
    title: item.title,
    description: item.description.value,
    step: index + 1,
}))

const desktopOrientation = computed(() => {
    if ((isDesktop.value || isTablet.value) && desktopLayout === EDesktopLayout.HORIZONTAL.toUpperCase()) {
        return EDesktopLayout.HORIZONTAL
    }
    return EDesktopLayout.VERTICAL
})
</script>

<template>
    <section
        ref="stepsRef"
        class="space-standard"
    >
        <div class="container">
            <div class="container-grid">
                <div
                    class="col-span-4 flex flex-col justify-center"
                    :class="[
                        {
                            'max-md:order-2': mobileImagePosition === EMobileImagePosition.TOP,
                            'md:order-2 md:col-start-6 lg:col-start-7':
                                desktopImagePosition === EDesktopImagePosition.LEFT && imageOrVideo,
                        },
                        imageOrVideo ? 'md:col-span-3 lg:col-span-5 lg:col-start-2' : 'md:col-span-8 lg:col-span-12',
                    ]"
                >
                    <VProgressSteps
                        v-model="currentStep"
                        class="w-full"
                        data-test="steps"
                        size="MEDIUM"
                        :dark="darkmode"
                        :orientation="desktopOrientation"
                        :steps="formattedStepsData"
                        :class="[
                            {
                                'max-md:mt-lg': mobileImagePosition === EMobileImagePosition.TOP && imageOrVideo,
                            },
                        ]"
                    />
                    <BaseButton
                        v-if="button"
                        class="mt-lg lg:mt-xl self-start"
                        :data-test="`steps-${button.label}`"
                        :variant="button.visualVariant"
                        size="MEDIUM"
                        :leading-icon="button?.leadingIcon"
                        :trailing-icon="button?.icon"
                        :dark="darkmode"
                        :href="button.action.url"
                        :target="getLinkTarget(button.action)"
                        @click="openDrawer(button.action?.drawer, page)"
                    >
                        {{ button.label }}
                    </BaseButton>
                    <VLink
                        v-if="link"
                        class="mt-lg lg:mt-xl self-start"
                        :href="link.action.url"
                        size="MEDIUM"
                        data-test="link"
                        :download="link.action?.downloadedAsset?.asset?.links?.site?.href"
                        :target="link.action?.openInNewTab ? '_blank' : '_self'"
                        @click="openDrawer(link.action?.drawer, page)"
                    >
                        {{ link.label }}
                        <VIcon
                            v-if="link.visualVariant === 'chevron'"
                            name="chevron-right"
                            size="SMALL"
                            data-test="STEPS_LINK-CHEVRON"
                        />
                    </VLink>
                </div>
                <div
                    v-if="imageOrVideo"
                    class="col-span-4 flex items-center md:col-span-4 lg:col-span-5"
                    :class="[
                        {
                            'max-md:order-1': mobileImagePosition === EMobileImagePosition.TOP,
                        },
                        desktopImagePosition === EDesktopImagePosition.LEFT
                            ? 'md:order-1'
                            : 'md:col-start-5 lg:col-start-8',
                    ]"
                >
                    <BaseImage
                        class="h-auto w-full rounded-2xl object-contain"
                        :image-data="responsiveImage"
                        :alt="imageOrVideo?.altText"
                        :picture-classes="{ 'max-md:mt-lg': mobileImagePosition === EMobileImagePosition.BOTTOM }"
                    />
                </div>
            </div>
        </div>
    </section>
</template>
